<template>
  <section
    ref="statsSectionRef"
    class="relative"
    :style="{ height: pinHeight + 'px' }"
  >
    <div class="sticky top-16 z-10 bg-white h-[calc(100vh-4rem)] w-full">
      <div
        class="relative overflow-x-hidden w-full h-full [clip-path:inset(0)]"
      >
        <div class="px-4 pt-12 pb-2 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div class="mb-8 text-center sm:mb-12">
            <h2
              :class="[
                'text-4xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] sm:text-5xl leading-tight sm:leading-tight mb-2 transition-opacity duration-700',
                inView ? 'opacity-100' : 'opacity-0',
              ]"
            >
              Faça a gestão completa da sua empresa,
            </h2>
            <h2
              :class="[
                'text-4xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] sm:text-5xl leading-tight sm:leading-tight mb-2 transition-opacity duration-700',
                inView ? 'opacity-100' : 'opacity-0',
              ]"
            >
              conte com a gente.
            </h2>
          </div>
          <div
            class="grid grid-cols-1 gap-8 items-start lg:grid-cols-2 md:gap-10 xl:gap-16"
          >
            <div
              :class="[
                'min-w-0 transition-transform transition-opacity duration-700 will-change-transform',
                inView
                  ? 'opacity-100 translate-x-0'
                  : 'opacity-0 -translate-x-12',
              ]"
              ref="leftColRef"
            >
              <div class="flex flex-col justify-between h-full">
                <ul class="space-y-8">
                  <li v-for="(item, index) in localItems" :key="item.key">
                    <button
                      type="button"
                      class="flex gap-4 items-start w-full text-left"
                      :aria-current="index === activeIndex ? 'true' : undefined"
                      @click="setActive(index)"
                    >
                      <span
                        class="mt-1 w-1 rounded-full transition-colors duration-300"
                        :class="
                          index === activeIndex
                            ? 'h-10 bg-yellow-400'
                            : 'h-10 bg-gray-200'
                        "
                      ></span>
                      <div class="flex-1">
                        <h3
                          class="text-2xl font-black sm:text-3xl"
                          :class="
                            index === activeIndex
                              ? 'text-kali-blue-600'
                              : 'text-kali-blue-500 hover:text-kali-blue-600'
                          "
                          :ref="index === 0 ? 'headingRef' : null"
                        >
                          {{ item.menuTitle || item.title }}
                        </h3>
                        <Transition name="fade-soft-text" mode="out-in">
                          <p
                            v-if="index === activeIndex"
                            class="mt-2 max-w-md text-base text-gray-700 sm:text-lg"
                          >
                            {{ item.menuDescription || item.description }}
                          </p>
                        </Transition>
                      </div>
                    </button>
                  </li>
                </ul>
              </div>
            </div>
            <div
              :class="[
                'relative hidden md:flex min-w-0 justify-center lg:justify-end lg:ml-0 transition-transform transition-opacity duration-700 will-change-transform',
                inView
                  ? 'opacity-100 translate-x-0'
                  : 'opacity-0 translate-x-12',
              ]"
            >
              <div
                class="relative w-full max-w-full md:max-w-xl lg:max-w-2xl aspect-[4/3] rounded-2xl overflow-hidden"
                :style="{ marginTop: imageOffset + 'px' }"
              >
                <div
                  class="grid w-full h-full transition-transform duration-500 ease-out"
                  :style="{
                    gridTemplateRows: `repeat(${localItems.length}, 100%)`,
                    transform: `translateY(-${activeIndex * 100}%)`,
                  }"
                >
                  <div
                    v-for="item in localItems"
                    :key="item.key"
                    class="w-full h-full"
                  >
                    <img
                      :src="item.image"
                      :alt="item.alt || 'Imagem'"
                      width="800"
                      height="600"
                      class="block object-contain w-full h-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from "vue";
import gestaoDeVendas from "@/assets/gestao-de-vendas.webp";
import gestaoDeEstoque from "@/assets/gestao-de-estoque.webp";
import gestaoEcommerce from "@/assets/gestao-ecommerce.webp";

const props = defineProps({
  items: {
    type: Array,
    default: () => [
      {
        key: "nota-fiscal",
        badge: "Notas fiscais",
        title: "Emissão de nota fiscal mais rápida e eficiente",
        description:
          "Além de fazer a gestão de pedidos em um só lugar, você emite notas fiscais (NFe, NFSe e NFCe) com mais rapidez e de forma automatizada.",
        image: gestaoDeVendas,
        alt: "Notas fiscais",
        ctaHref: "#",
      },
      {
        key: "estoque",
        badge: "Controle de estoque",
        title: "Controle de estoque integrado e automatizado",
        description:
          "Faça uma gestão otimizada do seu estoque. Automatize entradas e saídas e acompanhe métricas com relatórios, com o Kali Gestão",
        image: gestaoDeEstoque,
        alt: "Controle de estoque",
        ctaHref: "#",
      },
      {
        key: "integracoes",
        badge: "Integrações",
        title: "Integração com e-commerce e marketplace Mercado Livre",
        description:
          "Cadastre e integre seus produtos com sua loja virtual e conecte também no marketplace do Mercado Livre.",
        image: gestaoEcommerce,
        alt: "Integrações",
        ctaHref: "#",
      },
    ],
  },
});

const activeIndex = ref(0);
const localItems = ref([...props.items]);
const viewportHeight = ref(
  typeof window !== "undefined"
    ? window.innerHeight || document.documentElement.clientHeight || 0
    : 0,
);
// Deslocamento do sticky (top-16 = 4rem) — a section precisa reservar pelo
// menos essa altura de "janela presa" antes de poder soltar o scroll normal.
const STICKY_OFFSET = 64;
// Fração da altura da tela necessária para trocar de item — abaixo de 1
// para não exigir uma rolagem inteira de tela por solução.
const scrollPerItemRatio = ref(0.28);
const endDwellScreens = ref(0.5);
// A section só pode soltar o scroll (unstick) depois que a janela presa
// (altura da section - altura do card sticky) comportar todas as trocas de
// solução + o descanso no final. Caso contrário ela solta antes de mostrar
// a última solução, cortando o conteúdo.
function computePinHeight() {
  const vh = viewportHeight.value || 1;
  const stickyWindow = Math.max(0, vh - STICKY_OFFSET);
  const switchDistance =
    vh * scrollPerItemRatio.value * Math.max(0, localItems.value.length - 1);
  const dwellDistance = vh * endDwellScreens.value;
  return stickyWindow + switchDistance + dwellDistance;
}
const pinHeight = ref(computePinHeight());
const leftColRef = ref(null);
const headingRef = ref(null);
const imageOffset = ref(0);

function setActive(index) {
  activeIndex.value = index;
}

const statsSectionRef = ref(null);
const inView = ref(false);
let io = null;
let ticking = false;

function updateActiveFromScroll() {
  if (!statsSectionRef.value || !localItems.value.length) return;
  const rect = statsSectionRef.value.getBoundingClientRect();
  const sectionTop = rect.top;
  const scrolled = Math.max(0, Math.min(pinHeight.value, -sectionTop));
  const step = (viewportHeight.value || 1) * scrollPerItemRatio.value;
  const index = Math.floor(scrolled / step);
  activeIndex.value = Math.min(localItems.value.length - 1, Math.max(0, index));
}

function updateImageOffset() {
  try {
    if (!leftColRef.value || !headingRef.value) {
      imageOffset.value = 0;
      return;
    }
    const leftRect = leftColRef.value.getBoundingClientRect();
    const headingRect = headingRef.value.getBoundingClientRect();
    const offset = Math.max(0, Math.round(headingRect.top - leftRect.top));
    imageOffset.value = offset;
  } catch {
    imageOffset.value = 0;
  }
}

function handleScroll() {
  if (!inView.value) return;
  if (ticking) return;
  ticking = true;
  window.requestAnimationFrame(() => {
    updateActiveFromScroll();
    ticking = false;
  });
}

function updateDimensions() {
  viewportHeight.value =
    window.innerHeight || document.documentElement.clientHeight || 0;
  pinHeight.value = computePinHeight();
  nextTick(updateImageOffset);
}

onMounted(() => {
  io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        inView.value = entry.isIntersecting;
      });
    },
    { threshold: 0.1, root: null },
  );
  if (statsSectionRef.value) io.observe(statsSectionRef.value);
  updateDimensions();
  updateActiveFromScroll();
  window.addEventListener("scroll", handleScroll, { passive: true });
  window.addEventListener("resize", updateDimensions, { passive: true });
});

onBeforeUnmount(() => {
  if (io) io.disconnect();
  window.removeEventListener("scroll", handleScroll);
  window.removeEventListener("resize", updateDimensions);
});
</script>

<style scoped>
/* Texto: fade mais suave vindo da esquerda */
.fade-soft-text-enter-active,
.fade-soft-text-leave-active {
  transition:
    opacity 650ms cubic-bezier(0.25, 0.8, 0.25, 1),
    transform 650ms cubic-bezier(0.25, 0.8, 0.25, 1);
  will-change: opacity, transform;
}

.fade-soft-text-enter-from,
.fade-soft-text-leave-to {
  opacity: 0;
  transform: translateX(-6px);
}

/* Imagem: crossfade imperceptível (sem movimento) */
.fade-cross-enter-active,
.fade-cross-leave-active {
  transition: opacity 350ms ease-in-out;
  will-change: opacity;
}

.fade-cross-enter-from,
.fade-cross-leave-to {
  opacity: 0;
}
</style>
